import React from 'react';
import { ConsumptionDataSection } from './data-alignment/ConsumptionDataSection';
import { PriceDataSection } from './data-alignment/PriceDataSection';
import { DataAlignmentValidation } from './data-alignment/DataAlignmentValidation';
import { useDataAlignment } from '../hooks/useDataAlignment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Completion methods configuration - matches backend DataCompleter.METHODS
const completionMethods = [
  {
    id: 'none',
    label: 'Transform Only',
    description: 'Apply shift/duplicate transformations without pattern-based completion'
  },
  {
    id: 'pattern',
    label: 'Pattern-based (Recommended)',
    description: 'Uses average hourly patterns from existing data - simple and effective'
  },
  {
    id: 'seasonal',
    label: 'Seasonal Pattern',
    description: 'Accounts for seasonal variations, weekend/weekday differences, and hourly patterns'
  },
  {
    id: 'forward_fill',
    label: 'Forward Fill',
    description: 'Carries last known values forward - good for slowly changing data'
  },
  {
    id: 'interpolate',
    label: 'Linear Interpolation',
    description: 'Smoothly interpolates between known values - good for continuous data'
  }
];

// All helper functions moved to:
// - utils/dataAlignmentHelpers.ts (parsing, value extraction)
// - utils/dataStatistics.ts (calculateDataStats)
// - utils/billValidation.ts (validateBillConsumption)

interface DataAlignmentProps {
  onValidationChange?: (isValid: boolean) => void;
  projectId?: string;
  originalData?: any;
  alignedData?: any;
  onDataAligned?: () => Promise<void>;
}

export const DataAlignment: React.FC<DataAlignmentProps> = ({
  onValidationChange,
  projectId,
  originalData,
  alignedData,
  onDataAligned
}) => {
  // Use custom hook for all state management and calculations
  const dataAlignment = useDataAlignment({
    projectId,
    originalData,
    alignedData,
    onDataAligned
  });

  return (
    <div className="space-y-6">
      <ConsumptionDataSection
        projectId={projectId}
        consumption={dataAlignment.consumption}
        consumptionStats={dataAlignment.consumptionStats}
        billValidation={dataAlignment.billValidation}
        isAligned={dataAlignment.isConsumptionAligned}
        selectedMethod={dataAlignment.consumptionMethod}
        appliedMethod={dataAlignment.appliedConsumptionMethod}
        showOriginal={dataAlignment.showOriginalConsumption}
        completionMethods={completionMethods}
        onMethodChange={dataAlignment.setConsumptionMethod}
        onComplete={(shiftMonths, duplicateToYear, normalizeToYear) =>
          dataAlignment.handleAlignData('consumption', dataAlignment.consumptionMethod, shiftMonths, duplicateToYear, normalizeToYear)
        }
        onToggle={() => dataAlignment.toggleOriginalView('consumption')}
        onRevert={() => dataAlignment.handleRevertData('consumption')}
      />

      <PriceDataSection
        projectId={projectId}
        prices={dataAlignment.prices}
        priceStats={dataAlignment.priceStats}
        isAligned={dataAlignment.isPriceAligned}
        selectedMethod={dataAlignment.priceMethod}
        appliedMethod={dataAlignment.appliedPriceMethod}
        showOriginal={dataAlignment.showOriginalPrice}
        completionMethods={completionMethods}
        onMethodChange={dataAlignment.setPriceMethod}
        onComplete={(shiftMonths, duplicateToYear, normalizeToYear) =>
          dataAlignment.handleAlignData('price', dataAlignment.priceMethod, shiftMonths, duplicateToYear, normalizeToYear)
        }
        onToggle={() => dataAlignment.toggleOriginalView('price')}
        onRevert={() => dataAlignment.handleRevertData('price')}
      />

      <DataAlignmentValidation
        consumption={dataAlignment.validationConsumptionData}
        prices={dataAlignment.validationPriceData}
        onValidationChange={onValidationChange}
      />
    </div>
  );
};